// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";
import { Button, Form } from "react-bootstrap";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction } from "actions";
import { DataState, UserStateAWSError } from "types";

import IconUser from "icons/imgUser";
import LoginErrorStatus from "./LoginErrorStatus";

interface IUserMfaCodeProps {
  dataState: DataState;
  userEmail: string | undefined;
  awsErrorFeedback: UserStateAWSError | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: IUserMfaCodeProps = {
    dataState: state.scheduler.loginDataState,
    userEmail: state.gui.login.email,
    awsErrorFeedback: state.gui.login.userStateAWSFeedback,
  };
  return props;
};

interface IUserMfaCodeState {
  code: string;
}

interface IUserMfaCodeDispatch {
  onConfirmCode: (code: string) => void;
  onCancel: (username: string) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IUserMfaCodeDispatch = {
    onConfirmCode: (code: string) => {
      dispatch(raiseAction(ActionType.ACT_MFA_CONFIRM_REQ, code));
    },
    onCancel: (username: string) => {
      dispatch(raiseAction(ActionType.ACT_BACK_TO_LOGIN, username));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({});

class UserMfaCodeComponent extends React.Component<
  IUserMfaCodeProps & IUserMfaCodeDispatch,
  IUserMfaCodeState
> {
  state: IUserMfaCodeState = {
    code: "",
  };

  public render() {
    return (
      <div id="user">
        <div id="user_inner">
          <IconUser width={50} />
          <div className="user_right">
            <h1>
              <FormattedMessage id="Two-Factor Authentication" />
            </h1>
            <p>
              <FormattedMessage id="Please enter the code from your authenticator app." />
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <table>
                <tbody>
                  <tr>
                    <td>
                      <FormattedMessage id="Code" />:
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        name="mfaCode"
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        value={this.state.code}
                        autoFocus
                        disabled={this.props.dataState === DataState.SAVE_REQ}
                        onChange={(event) => {
                          this.setState({ code: event?.currentTarget.value });
                        }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div id="buttonbox" className="mt-3">
                <Button
                  id="confirmMfaCode"
                  type="submit"
                  variant="primary"
                  className="me-2"
                  disabled={this.props.dataState === DataState.SAVE_REQ || !this.isCodeValid()}
                  onClick={(_event) => this.props.onConfirmCode(this.state.code.trim())}
                >
                  <FormattedMessage id="Confirm" />
                </Button>
                {this.props.dataState !== DataState.SAVE_REQ && (
                  <Button
                    variant="secondary"
                    onClick={(_event) => this.props.onCancel(this.props.userEmail ?? "")}
                  >
                    <FormattedMessage id="Cancel" />
                  </Button>
                )}
              </div>
            </form>
            {this.props.dataState === DataState.SAVE_FAIL &&
              (this.props.awsErrorFeedback === UserStateAWSError.CODE_MISMATCH ?
                <div className="alert alert-danger mt-2">
                  <FormattedMessage id="The entered code is not valid - please try again." />
                </div>
              : <LoginErrorStatus />)}
          </div>
        </div>
      </div>
    );
  }

  private isCodeValid() {
    return /^\d{6}$/.test(this.state.code.trim());
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserMfaCodeComponent);
